import { PropsWithChildren } from "react";

import { css } from "@compiled/react";
import Slider from "react-slick";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";

import { useHeroState } from "./util/hero-context";

import type { Settings as SlickSettings } from "react-slick";

const LeftArrow = ({ onClick }: { onClick?: () => void }) => (
    <button css={buttonLeft} onClick={onClick}>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="65"
            height="64"
            viewBox="0 0 65 64"
            fill="none"
        >
            <path
                d="M56.6172 32C56.6172 18.6667 45.9505 8 32.6172 8C19.2838 8 8.61718 18.6667 8.61719 32C8.61719 45.3333 19.2839 56 32.6172 56C45.9505 56 56.6172 45.3333 56.6172 32Z"
                fill="#101214"
            />
            <path
                d="M35.2842 20.2667L39.0176 24.0001L31.0176 32.0001L39.0176 40.0001L35.2842 43.7334L23.5509 32.0001L35.2842 20.2667Z"
                fill="white"
            />
        </svg>
    </button>
);

const RightArrow = ({ onClick }: { onClick?: () => void }) => (
    <button css={buttonRight} onClick={onClick}>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="#none"
        >
            <path
                d="M8 32C8 45.3333 18.6667 56 32 56C45.3333 56 56 45.3333 56 32C56 18.6667 45.3333 8 32 8C18.6667 8 8 18.6667 8 32Z"
                fill="#101214"
            />
            <path
                d="M29.3329 43.7333L25.5996 39.9999L33.5996 31.9999L25.5996 23.9999L29.3329 20.2666L41.0663 31.9999L29.3329 43.7333Z"
                fill="white"
            />
        </svg>
    </button>
);

const settings: SlickSettings = {
    prevArrow: <LeftArrow />,
    nextArrow: <RightArrow />
};

export const ProductModalCarousel = ({ children }: PropsWithChildren) => {
    const { modalIndex } = useHeroState();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const beforeChange = (currentSlide: number, nextSlide: number) => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            attributes: {
                name: "atlas-55799-carousel-slide-button-clicked",
                section: "hero-section",
                slide: currentSlide,
                nextSlide: nextSlide
            }
        }).fire();
    };

    return (
        <div css={modalBackgroundStyles}>
            <div css={[slickStyle, sliderParent]}>
                <Slider
                    {...settings}
                    initialSlide={modalIndex}
                    beforeChange={beforeChange}
                >
                    {children}
                </Slider>
            </div>
        </div>
    );
};

const modalBackgroundStyles = css({
    position: "fixed",
    insetInlineStart: "0",
    insetBlockStart: "0",
    insetInlineEnd: "0",
    insetBlockEnd: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
        "var(--DarkNeutral-Opaque-DarkNeutral-100, rgba(16, 18, 20, 0.85))",
    zIndex: "10",
    [media.below.lg]: {
        display: "none"
    }
});

const sliderParent = css({
    width: "80%"
});

// Source https://github.com/kenwheeler/slick/blob/master/slick/slick.css
const slickStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".slick-track"]: {
        display: "flex"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".slick-list"]: {
        margin: " 0 -60px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".slick-list:focus"]: {
        outline: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".slick-slide"]: {
        display: "flex",
        margin: "0 60px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".slick-initialized .slick-slide"]: {
        display: "block"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".slick-loading .slick-slide"]: {
        visibility: "hidden"
    }
});

const arrowButton = css({
    position: "absolute",
    border: "none",
    cursor: "pointer",
    zIndex: 1,
    background: "transparent",
    insetBlockStart: "50%"
});

const buttonLeft = css({
    ...arrowButton,
    insetInlineStart: "9%"
});

const buttonRight = css({
    ...arrowButton,
    insetInlineEnd: "9%"
});
